import React, { useState } from 'react';
import { Button, Modal, Radio, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMember } from '../../../common/member/MemberContext';
import { MemberProfile } from '../../../model/members/MemberProfile';
import { PointOfSaleTransaction } from '../../../model/pointofsale/PointOfSaleTransaction';
import { MembershipCategory } from '../../../model/members/MembershipCategory';
import CreateJoiningFeeTransaction from './CreateJoiningFeeTransaction';
import CreateMembershipSubscriptionTransaction from './CreateMembershipSubscriptionTransaction';

interface CreateMembershipTransactionProps {
  member: MemberProfile;
  pointOfSaleTransactions: PointOfSaleTransaction[];
  membershipCategories: MembershipCategory[];
  onTransactionUpdated?: () => void;
  onMemberUpdated?: () => void;
  // Full-width, theme-coloured trigger for the Member Profile V2 "Quick actions"
  // rail (so it lines up with the other block buttons).
  block?: boolean;
}

type TransactionType = 'joining-fee' | 'subscription';

const CreateMembershipTransaction: React.FC<CreateMembershipTransactionProps> = ({
  member,
  pointOfSaleTransactions,
  membershipCategories,
  onTransactionUpdated,
  onMemberUpdated,
  block = false
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [transactionType, setTransactionType] = useState<TransactionType>('subscription');
  const { isAdmin } = useMember();

  if (!isAdmin) {
    return null;
  }

  const showModal = () => {
    setIsModalVisible(true);
    setTransactionType('subscription'); // Reset to default
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleTransactionTypeChange = (e: any) => {
    setTransactionType(e.target.value);
  };

  return (
    <>
      <Button
        type="primary"
        block={block}
        icon={<PlusOutlined />}
        onClick={showModal}
        style={block ? undefined : { backgroundColor: '#1890ff' }}
      >
        Add Membership Transaction
      </Button>

      <Modal
        title="Add Membership Transaction"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>
              Select Transaction Type: *
            </div>
            <Radio.Group
              value={transactionType}
              onChange={handleTransactionTypeChange}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio value="joining-fee" style={{ fontSize: '14px', padding: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>New Member Fees Transaction</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Create a transaction for initial member joining fee with pro-rata calculation
                    </div>
                  </div>
                </Radio>
                <Radio value="subscription" style={{ fontSize: '14px', padding: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>Membership Payment</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Create a transaction for membership subscription payment for a specific month
                    </div>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          {transactionType === 'joining-fee' && (
            <CreateJoiningFeeTransaction
              member={member}
              pointOfSaleTransactions={pointOfSaleTransactions}
              membershipCategories={membershipCategories}
              onTransactionUpdated={() => {
                if (onTransactionUpdated) {
                  onTransactionUpdated();
                }
                handleCancel();
              }}
              onMemberUpdated={onMemberUpdated}
              hideButton={true}
              parentModalVisible={isModalVisible}
              onParentCancel={handleCancel}
            />
          )}

          {transactionType === 'subscription' && (
            <CreateMembershipSubscriptionTransaction
              member={member}
              pointOfSaleTransactions={pointOfSaleTransactions}
              membershipCategories={membershipCategories}
              onTransactionUpdated={() => {
                if (onTransactionUpdated) {
                  onTransactionUpdated();
                }
                handleCancel();
              }}
              onMemberUpdated={onMemberUpdated}
              hideButton={true}
              parentModalVisible={isModalVisible}
              onParentCancel={handleCancel}
            />
          )}
        </Space>
      </Modal>
    </>
  );
};

export default CreateMembershipTransaction;

